const CART_CHANGE_PATH = '/cart/change.js';

// A number input's spinner fires a change event per click, so a rapid 1->4
// is three events in quick succession. Wait for the value to settle before
// sending, rather than racing a request per click. Blur flushes immediately,
// so this window only delays spinner clicks, never a typed value.
const QTY_SETTLE_MS = 300;

let requestInFlight = false;

// The Braze connector logs the cart_updated event into the SDK's localStorage
// queue 25-50ms after the /cart/change.js response (measured on UAT, six
// removals). Once queued it is safe: a queued event survives a reload and the
// SDK on the next page sends it.
//
// What is NOT safe is reloading while a flush request is in flight. The SDK
// removes events from the queue when it builds the request and only re-queues
// them on XHR error/timeout, never on the abort a navigation causes. Braze
// round trips on UAT ranged 250-1200ms, so a fixed reload timer after a
// blind flush kick lost events roughly whenever Braze was slow. Instead:
// wait for the connector to log, flush, and reload only once the flush has
// completed (callback), with a fallback so an unresponsive SDK can never
// leave the customer on a stale cart page.
const LOG_SETTLE_MS = 150;
const RELOAD_FALLBACK_MS = 2000;

const scheduleReload = () => {
    let reloaded = false;
    const reload = () => {
        if (reloaded) {
            return;
        }
        reloaded = true;
        // A quantity edit may have landed while the reload was pending;
        // apply it rather than reloading over it.
        if (dirtyQtyInputs().length) {
            processQtyQueue();
            return;
        }
        window.location.reload();
    };

    setTimeout(() => {
        const braze = window.braze;
        if (!braze || typeof braze.requestImmediateDataFlush !== 'function') {
            reload();
            return;
        }
        braze.requestImmediateDataFlush(reload);
        setTimeout(reload, RELOAD_FALLBACK_MS);
    }, LOG_SETTLE_MS);
};

// The old form submit navigated immediately, so the fresh page's bag count
// painted before Bold finished re-pricing. The AJAX flow reloads later, so
// the badge is updated here from the response's absolute item_count to keep
// that feedback immediate. Deliberately not the header's 'cart-change' event,
// which applies a relative delta and would be wrong for removals.
const updateBagCount = cart => {
    if (!cart || typeof cart.item_count !== 'number') {
        return;
    }
    const headerCart = document.getElementById('header-cart');
    if (!headerCart) {
        return;
    }
    const bagQuantity = headerCart.getElementsByClassName('cart__mini-quantity')[0];
    if (bagQuantity) {
        bagQuantity.innerHTML = cart.item_count;
    }
    if (cart.item_count > 0) {
        headerCart.classList.add('cart__mini-active');
    } else {
        headerCart.classList.remove('cart__mini-active');
    }
};

const cartChangeRequest = body => {
    return fetch(CART_CHANGE_PATH, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(body),
    }).then(response => {
        if (!response.ok) {
            throw new Error('Cart change failed with status ' + response.status);
        }
        return response.json().catch(() => null);
    }).then(cart => {
        updateBagCount(cart);
    });
};

const removeLinks = document.querySelectorAll('[data-cart-remove]');
removeLinks.forEach(removeLink => {
    removeLink.addEventListener('click', event => {
        const row = removeLink.closest('.cart__product');
        const qtyInput = row && row.querySelector('.cart__item-qty');
        if (!qtyInput) {
            return;
        }
        event.preventDefault();
        row.classList.add('cart__product--removing');
        qtyInput.value = 0;
        clearTimeout(settleTimer);
        flushQtyChanges();
    });
});

const submitCartForm = () => {
    const submitUpdate = document.querySelector('#cart__submit-update');
    if (submitUpdate) {
        submitUpdate.click();
    }
};

// Quantity changes track desired state, not events: an input is dirty while
// its value differs from the last quantity the server confirmed for it. The
// queue keeps sending until nothing is dirty, so an edit made at any moment
// (mid-request, pre-reload) is picked up by the next sweep instead of being
// dropped. Requests are keyed by line item key, not line index, because a
// queued quantity of 0 removes a row and shifts every later line index.
const qtyInputs = Array.from(document.querySelectorAll('.cart__item-qty'));

const confirmedQty = qtyInput => {
    const sent = qtyInput.dataset.sentQty;
    return parseInt(sent != null ? sent : qtyInput.defaultValue, 10);
};

const dirtyQtyInputs = () => {
    return qtyInputs.filter(qtyInput => {
        const current = parseInt(qtyInput.value, 10);
        return isNaN(current) || current < 0 || current !== confirmedQty(qtyInput);
    });
};

const processQtyQueue = () => {
    const qtyInput = dirtyQtyInputs()[0];
    if (!qtyInput) {
        scheduleReload();
        return;
    }

    const key = qtyInput.dataset.key;
    const quantity = parseInt(qtyInput.value, 10);
    if (!key || isNaN(quantity) || quantity < 0) {
        submitCartForm();
        return;
    }

    cartChangeRequest({ id: key, quantity: quantity })
        .then(() => {
            qtyInput.dataset.sentQty = quantity;
            processQtyQueue();
        })
        .catch(() => {
            requestInFlight = false;
            submitCartForm();
        });
};

let settleTimer = null;

const flushQtyChanges = () => {
    settleTimer = null;
    if (requestInFlight) {
        // An active request's completion sweep (or the pre-reload check)
        // will pick the dirty input up.
        return;
    }
    if (!dirtyQtyInputs().length) {
        return;
    }
    requestInFlight = true;
    processQtyQueue();
};

qtyInputs.forEach(qtyInput => {
    qtyInput.addEventListener('change', () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(flushQtyChanges, QTY_SETTLE_MS);
    });

    qtyInput.addEventListener('blur', () => {
        if (settleTimer) {
            clearTimeout(settleTimer);
            flushQtyChanges();
        }
    });
});
